import { useEffect } from 'react';
import posthog from 'posthog-js';
import { useAuth } from './useAuth';

// Initialize PostHog only when a real project key is configured.
// Avoids 401/404 console errors from placeholder keys hitting PostHog endpoints.
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://app.posthog.com';
const POSTHOG_ENABLED =
  typeof window !== 'undefined' &&
  !!POSTHOG_KEY &&
  POSTHOG_KEY.startsWith('phc_') &&
  !POSTHOG_KEY.includes('test_key') &&
  !POSTHOG_KEY.includes('replace');

if (POSTHOG_ENABLED) {
  posthog.init(POSTHOG_KEY!, {
    api_host: POSTHOG_HOST,
    person_profiles: 'identified_only',
    loaded: (posthog) => {
      if (import.meta.env.DEV) posthog.debug();
    }
  });
}

// GA4 tracking helper
const gtag = (...args: any[]) => {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag(...args);
  }
};

export const useAnalytics = () => {
  const { user } = useAuth();

  useEffect(() => {
    if (POSTHOG_ENABLED && user) {
      posthog.identify(user.id, {
        email: user.email,
        name: user.user_metadata?.display_name || user.email,
      });
    }
  }, [user]);

  const track = (event: string, properties?: Record<string, any>) => {
    if (POSTHOG_ENABLED) posthog.capture(event, properties);
  };

  const trackPurchase = (tipId: string, amount: number, itemName: string, tipsterId?: string, sport?: string, confidence?: number) => {
    // PostHog
    track('purchase', { tip_id: tipId, amount, item_name: itemName });
    
    // GA4
    gtag('event', 'purchase', {
      transaction_id: 'txn_' + Date.now(),
      value: amount,
      currency: 'USD',
      items: [{
        item_id: tipId,
        item_name: itemName,
        price: amount,
        quantity: 1,
        ...(tipsterId && { tipster_id: tipsterId }),
        ...(sport && { sport }),
        ...(confidence && { confidence })
      }]
    });
  };

  const trackSubscribe = (subscriptionId: string, subscriptionName: string, value: number, planDuration: string) => {
    track('subscribe', { subscription_id: subscriptionId, subscription_name: subscriptionName, value });
    
    gtag('event', 'subscribe', {
      subscription_id: subscriptionId,
      subscription_name: subscriptionName,
      value,
      currency: 'USD',
      plan_duration: planDuration
    });
  };

  const trackLeaderboardView = (variant: string = 'global', timeframe: string = 'this_week') => {
    track('leaderboard_view', { variant, timeframe });
    
    gtag('event', 'leaderboard_view', { variant, timeframe });
  };

  const trackViewTip = (tipId: string, sport: string, confidence?: number) => {
    track('view_tip', { tip_id: tipId, sport, confidence });
    
    gtag('event', 'view_item', {
      items: [{
        item_id: tipId,
        item_name: 'Betting Tip',
        item_category: sport,
        ...(confidence && { confidence })
      }]
    });
  };

  const trackViewItem = (item: { id: string; name: string; category?: string; price?: number }) => {
    track('view_item', { item_id: item.id, item_name: item.name });
    
    gtag('event', 'view_item', {
      items: [{
        item_id: item.id,
        item_name: item.name,
        item_category: item.category || 'Bundle',
        price: item.price || 0,
        currency: 'USD'
      }]
    });
  };

  const trackAddToCart = (item: { id: string; name: string; price: number; category?: string }) => {
    track('add_to_cart', { item_id: item.id, item_name: item.name, price: item.price });
    
    gtag('event', 'add_to_cart', {
      items: [{
        item_id: item.id,
        item_name: item.name,
        item_category: item.category || 'Bundle',
        price: item.price,
        currency: 'USD',
        quantity: 1
      }]
    });
  };

  const trackTipCreate = (sport: string, league: string, confidence: number) => {
    track('tip_created', { sport, league, confidence });
  };

  const trackUserSignup = (method: string) => {
    track('user_signup', { method });
    gtag('event', 'sign_up', { method });
  };

  const trackPageView = (page: string) => {
    track('page_view', { page });
    gtag('event', 'page_view', { page_title: page });
  };

  return {
    track,
    trackPurchase,
    trackSubscribe,
    trackLeaderboardView,
    trackViewTip,
    trackViewItem,
    trackAddToCart,
    trackTipCreate,
    trackUserSignup,
    trackPageView
  };
};

export default posthog;